import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useToast} from '@/matic/components/ui/toast';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {trackProductEvent} from '@/matic/lib/product-observability';
import {relayKeys} from '../query-keys';
import type {RelayPin} from '../types/relay-types';
import {
  buildShortcutManifest,
  shortcutViewKey,
  SHORTCUTS_FOLDER_NAME,
} from '../lib/shortcuts/build-shortcut-manifest';
import {
  clearStoredDirectoryHandle,
  getStoredDirectoryHandle,
  setStoredDirectoryHandle,
} from '../lib/shortcuts/directory-handle-store';
import {
  detectShortcutFormat,
  type ShortcutFormat,
} from '../lib/shortcuts/shortcut-file-formats';
import {
  ensureWritePermission,
  isFileSystemAccessSupported,
  pickShortcutsDirectory,
  writeManifest,
  type WriteOutcome,
} from '../lib/shortcuts/file-system-writer';
import {buildShortcutZip, downloadBlob} from '../lib/shortcuts/zip-writer';
import {isBuiltinDefinitionId} from '../lib/shortcuts/shortcut-urls';
import {
  useViewDefinitions,
  useViewInstances,
  normalizeScopeKey,
} from '@/matic/features/agents/otto/views/hooks/useView';
import {
  readStoredValue,
  writeStoredValue,
} from '@/matic/lib/utils/local-storage';
import {usePins} from './usePins';

const INCLUDE_VIEWS_STORAGE_KEY = 'matic:shortcuts:include-views';
const SELECTED_VIEW_KEYS_STORAGE_KEY = 'matic:shortcuts:selected-view-keys';
const FORMAT_STORAGE_KEY = 'matic:shortcuts:format';

function readIncludeViews(): boolean {
  return readStoredValue(INCLUDE_VIEWS_STORAGE_KEY) === 'true';
}

function readSelectedViewKeys(): string[] | undefined {
  try {
    const stored = globalThis.localStorage.getItem(
      SELECTED_VIEW_KEYS_STORAGE_KEY
    );
    return stored ? (JSON.parse(stored) as string[]) : undefined;
  } catch {
    return undefined;
  }
}

function writeSelectedViewKeys(keys: Iterable<string>): void {
  try {
    globalThis.localStorage.setItem(
      SELECTED_VIEW_KEYS_STORAGE_KEY,
      JSON.stringify([...keys])
    );
  } catch {
    // Selection remains usable for this session when storage is unavailable.
  }
}

/**
 * Persisted for the same reason `selectedViewKeys` is: the rail's dialog and
 * the breadcrumb's pin-toast are independent mounted instances, and a write
 * triggered from whichever one has a stale in-memory `format` would rewrite
 * every shortcut with the wrong extension — `pruneStaleEntries` would then
 * delete the correctly-formatted files it can no longer see in the new
 * manifest. Reading fresh from storage at write time (see
 * `writeAfterPinsSettle`) is what actually closes that gap; this persistence
 * just means a *reload* remembers the override too.
 */
function readFormat(): ShortcutFormat {
  const stored = readStoredValue(FORMAT_STORAGE_KEY);
  return stored === 'url' || stored === 'webloc'
    ? stored
    : detectShortcutFormat();
}

function writeFormat(next: ShortcutFormat): void {
  writeStoredValue(FORMAT_STORAGE_KEY, next);
}

export type DesktopShortcutView = {
  key: string;
  pageName: string;
  definitionName: string;
};

export type DesktopShortcutPage = {id: string; name: string};

/**
 * At most one `writeManifest` call runs at a time, no matter which of the
 * (possibly several) mounted `useDesktopShortcuts()` instances triggered it —
 * two instances writing to the *same real directory* concurrently could
 * genuinely corrupt it, one write's prune racing another's write. Module
 * scope (an ES module is a singleton) makes this queue shared across every
 * instance.
 *
 * The rail's dialog and the breadcrumb's pin-toast still each hold their own
 * `isWorking`/`handle`/`includeViews`/`format` React state — lifting that
 * into a shared store (Context, a small Zustand store) so every instance
 * renders in lockstep is still a real, un-made design decision. What *is*
 * handled without it: every write path re-reads the settings that would be
 * destructive to get wrong — `pins`, `includeViews`, `format`, and the
 * directory `handle` itself — fresh from their source of truth (the query
 * cache, `localStorage`, IndexedDB) at write time rather than trusting
 * whichever instance's closure triggered the write. See `writeAfterPinsSettle`
 * and `ensureReady`. What's left un-shared is purely cosmetic: a `hasFolder`/
 * `folderName` display can lag in an instance that hasn't remounted since a
 * folder was chosen elsewhere, but no write is built from that stale display
 * value.
 */
let writeChain: Promise<void> = Promise.resolve();

/**
 * Chains `work` onto the shared tail and reassigns the tail — both
 * synchronously, with no `await` between reading `writeChain` and writing it
 * back.
 *
 * That ordering is required, not stylistic: an `await`-based rewrite of this
 * (`await writeChain; const result = work(); writeChain = ...`) has a gap
 * between the read and the reassignment. Two calls fired back-to-back
 * synchronously — exactly the shape of two mounted hook instances triggering
 * a write close together, the case this function exists for — both run up to
 * their `await` before either resumes, so both read the *same* pre-reassignment
 * `writeChain` and neither actually waits on the other. Caught by testing
 * this against a mock filesystem with two concurrent `writeManifest` calls:
 * the await version let them interleave.
 */
async function runSerialized<T>(work: () => Promise<T>): Promise<T> {
  // No internal `await` above this point — both `.then()` calls below run
  // synchronously, in the same tick as the read of `writeChain`, which is
  // what makes back-to-back calls actually serialize. `async` on the function
  // itself only satisfies promise-function-async and auto-wraps the return
  // in a Promise; it doesn't introduce a yield point on its own.
  // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-catch -- must stay synchronous, see docblock above.
  const result = writeChain.then(work, work);
  // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-catch -- same reason.
  writeChain = result.then(
    () => undefined,
    () => undefined
  );
  return result;
}

/**
 * Controller for the desktop-shortcuts export: what would be written, where it
 * goes, and the two actions that put it there.
 *
 * The dialog stays dumb and fully controlled, mirroring `useViewExport`.
 *
 * **Permission ordering is the load-bearing detail here.** Both actions call
 * into the picker or `ensureWritePermission` before awaiting anything else, so
 * the browser's transient user activation from the click is still live. Doing
 * data work first spends the activation and the permission prompt fails
 * silently.
 */
export function useDesktopShortcuts() {
  const {toast} = useToast();
  const queryClient = useQueryClient();
  const tenantId = useRequiredTenantId();
  const {data: pins} = usePins();
  const {data: viewInstances} = useViewInstances();
  const {data: viewDefinitions} = useViewDefinitions();

  const [handle, setHandle] = useState<FileSystemDirectoryHandle | undefined>();
  const [isWorking, setIsWorking] = useState(false);
  const workingRef = useRef(false);
  const [progress, setProgress] = useState<{written: number; total: number}>();
  const [format, setFormat] = useState<ShortcutFormat>(readFormat);
  const [selectedViewKeys, setSelectedViewKeys] = useState<Set<string>>(
    () => new Set(readSelectedViewKeys() ?? [])
  );
  const initializedSelection = useRef(false);

  const isSupported = useMemo(() => isFileSystemAccessSupported(), []);

  useEffect(() => {
    let cancelled = false;

    const loadStoredHandle = async () => {
      const stored = await getStoredDirectoryHandle(tenantId);
      if (!cancelled) setHandle(stored);
    };

    void loadStoredHandle();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const pages = useMemo<DesktopShortcutPage[]>(
    () => (pins ?? []).map((pin) => ({id: pin.id, name: pin.label})),
    [pins]
  );

  const setFormatPersisted = useCallback((next: ShortcutFormat) => {
    setFormat(next);
    writeFormat(next);
  }, []);

  const definitionLabels = useMemo(
    () => new Map((viewDefinitions ?? []).map((def) => [def.id, def.name])),
    [viewDefinitions]
  );

  const availableViews = useMemo<DesktopShortcutView[]>(() => {
    if (!pins || !viewInstances || !viewDefinitions) return [];
    const labels = new Map(
      viewDefinitions.map((definition) => [definition.id, definition.name])
    );
    const instancesByScope = new Map<string, Set<string>>();
    for (const instance of viewInstances) {
      const definitions =
        instancesByScope.get(instance.scopeKey) ?? new Set<string>();
      definitions.add(instance.definitionId);
      instancesByScope.set(instance.scopeKey, definitions);
    }

    return pins.flatMap((pin) => {
      const scopeKey = normalizeScopeKey(
        pin.payload.refs.map(
          (ref) => `${ref.objectTypeId}:${ref.objectRecordId}`
        )
      );
      return [...(instancesByScope.get(scopeKey) ?? [])].flatMap(
        (definitionId) => {
          const definitionName = labels.get(definitionId);
          if (!definitionName || isBuiltinDefinitionId(definitionId)) {
            return [];
          }

          return [
            {
              key: shortcutViewKey(scopeKey, definitionId),
              pageName: pin.label,
              definitionName,
            },
          ];
        }
      );
    });
  }, [pins, viewInstances, viewDefinitions]);

  useEffect(() => {
    if (
      initializedSelection.current ||
      !pins ||
      !viewInstances ||
      !viewDefinitions
    )
      return;
    initializedSelection.current = true;
    if (readSelectedViewKeys() !== undefined) return;

    // Migrate the previous global toggle once: people who opted into every
    // View retain that choice, while the default remains an empty selection.
    if (readIncludeViews()) {
      const next = new Set(availableViews.map((view) => view.key));
      setSelectedViewKeys(next);
      writeSelectedViewKeys(next);
    }
  }, [pins, viewInstances, viewDefinitions, availableViews]);

  const setViewSelected = useCallback((key: string, selected: boolean) => {
    setSelectedViewKeys((previous) => {
      const next = new Set(previous);
      if (selected) next.add(key);
      else next.delete(key);
      writeSelectedViewKeys(next);
      return next;
    });
  }, []);

  /**
   * `overrides` lets `writeAfterPinsSettle` build from freshly-read pins /
   * settings instead of this instance's (possibly stale) state — see there
   * for why that matters. Everything else (the dialog's live `selectedViewCount`,
   * `updateInPlace`) uses this instance's own state, which is already correct
   * for the surface that's actually rendering it.
   */
  const buildEntries = useCallback(
    (overrides?: {
      pins?: RelayPin[];
      selectedViewKeys?: ReadonlySet<string>;
      format?: ShortcutFormat;
    }) =>
      buildShortcutManifest({
        origin: globalThis.location.origin,
        pins: overrides?.pins ?? pins ?? [],
        format: overrides?.format ?? format,
        selectedViewKeys: overrides?.selectedViewKeys ?? selectedViewKeys,
        viewInstances: viewInstances ?? [],
        definitionLabels,
      }),
    [pins, format, selectedViewKeys, viewInstances, definitionLabels]
  );

  /**
   * How many View shortcuts the current toggle would add, for the dialog's
   * count line. Cheap — the manifest is a few dozen small strings.
   */
  const selectedViewCount = useMemo(
    () =>
      availableViews.filter((view) => selectedViewKeys.has(view.key)).length,
    [availableViews, selectedViewKeys]
  );

  const reportOutcome = useCallback(
    (outcome: WriteOutcome, formatUsed: ShortcutFormat) => {
      switch (outcome.status) {
        case 'written': {
          toast({
            title: `${outcome.fileCount} shortcuts saved`,
            variant: 'success',
          });
          trackProductEvent('shortcut.created', {
            fileCount: outcome.fileCount,
            format: formatUsed,
            delivery: 'folder',
          });
          break;
        }

        case 'denied': {
          toast({
            title: "Couldn't write to that folder",
            description: 'MATIC needs permission to save files there.',
            variant: 'error',
          });
          break;
        }

        case 'folder-missing': {
          void clearStoredDirectoryHandle(tenantId);
          setHandle(undefined);
          toast({
            title: 'That folder is gone',
            description: 'Choose a new one to save your shortcuts.',
            variant: 'error',
          });
          break;
        }

        case 'failed': {
          toast({
            title: "Couldn't save your shortcuts",
            description: outcome.message,
            variant: 'error',
          });
          break;
        }
      }
    },
    [toast, tenantId]
  );

  const runWrite = useCallback(
    async (
      target: FileSystemDirectoryHandle,
      overrides?: {
        pins?: RelayPin[];
        selectedViewKeys?: ReadonlySet<string>;
        format?: ShortcutFormat;
      }
    ) => {
      // Synchronous, unlike `isWorking` — rejects a second click landing
      // before React has re-rendered the disabled button.
      if (workingRef.current) return;
      workingRef.current = true;
      setIsWorking(true);
      setProgress(undefined);
      const formatUsed = overrides?.format ?? format;
      try {
        // Serialized across every mounted instance — see `runSerialized`.
        const outcome = await runSerialized(async () =>
          writeManifest(target, buildEntries(overrides), (written, total) => {
            setProgress({written, total});
          })
        );
        reportOutcome(outcome, formatUsed);
      } finally {
        workingRef.current = false;
        setIsWorking(false);
        setProgress(undefined);
      }
    },
    [buildEntries, reportOutcome, format]
  );

  /** Pick a folder (or a different one) and write into it. */
  const chooseFolderAndWrite = useCallback(async () => {
    const picked = await pickShortcutsDirectory();

    if (picked.status === 'cancelled') return;

    if (picked.status === 'blocked') {
      toast({
        title: 'Saving folders is blocked in this browser',
        description: 'Desktop shortcuts need Chrome or Edge.',
        variant: 'error',
      });
      return;
    }

    if (picked.status === 'failed') {
      toast({
        title: "Couldn't open that folder",
        description: picked.message,
        variant: 'error',
      });
      return;
    }

    setHandle(picked.handle);
    await setStoredDirectoryHandle(tenantId, picked.handle);
    await runWrite(picked.handle);
  }, [runWrite, toast, tenantId]);

  /**
   * Download the same manifest as a zip. No stored folder and no update-in-place
   * — the user unpacks it wherever they like and re-downloads to refresh.
   */
  const downloadZip = useCallback(() => {
    const entries = buildEntries();
    downloadBlob(buildShortcutZip(entries), `${SHORTCUTS_FOLDER_NAME}.zip`);
    toast({
      title: `${entries.length} shortcuts downloaded`,
      description: `Unzip ${SHORTCUTS_FOLDER_NAME} wherever you want it.`,
      variant: 'success',
    });
    trackProductEvent('shortcut.created', {
      fileCount: entries.length,
      format,
      delivery: 'zip',
    });
  }, [buildEntries, format, toast]);

  /**
   * Take write permission, nothing else.
   *
   * Split out from `updateInPlace` for callers that must do async work between
   * the click and the write — creating a Page, then waiting for the pins query
   * to include it. `requestPermission` needs live transient user activation, so
   * it has to happen first; once granted it holds for the page session, so the
   * eventual write prompts nothing.
   *
   * Resolves the handle from storage when this instance's own state hasn't
   * seen it yet (e.g. it was chosen from the rail's dialog after this
   * instance — say, the breadcrumb's — already mounted), rather than trusting
   * `handle` alone. Without this, a second mounted instance would report "no
   * folder" even though one exists, and silently no-op the write it's gating.
   *
   * Returns false when the user declined, so callers can carry on with whatever
   * else they were doing rather than treating it as a failure.
   */
  const ensureReady = useCallback(async () => {
    const target = handle ?? (await getStoredDirectoryHandle(tenantId));
    if (!target) return false;
    if (!handle) setHandle(target);

    const permission = await ensureWritePermission(target);
    if (permission === 'denied') {
      reportOutcome({status: 'denied'}, format);
      return false;
    }

    return true;
  }, [handle, tenantId, reportOutcome, format]);

  /** Rewrite the folder the user already chose. */
  const updateInPlace = useCallback(async () => {
    // Permission first, before any other await — see the docblock above.
    if (!(await ensureReady())) return;
    const target = handle ?? (await getStoredDirectoryHandle(tenantId));
    if (target) await runWrite(target);
  }, [ensureReady, handle, tenantId, runWrite]);

  /**
   * Wait for the pins list to reflect a just-created Page, then write.
   *
   * **Callers must call `ensureReady()` themselves first, synchronously from
   * the same click that created the Page** — that part can't be centralized
   * here, because it has to run before the caller's own first `await`, and
   * this hook doesn't own that call site.
   *
   * This is the one write path that regularly runs from an instance other
   * than the one whose dialog you're looking at (the breadcrumb's pin-toast
   * can fire after the rail configured Views/format), so every value it
   * writes with is re-read fresh rather than taken from this instance's own
   * closures:
   *
   * - `pins`: `createPin` invalidates in `onSettled`, which runs after
   *   `onSuccess`, so a caller that just resolved a create mutation cannot
   *   trust the pins cache yet without an explicit refetch — and this
   *   instance's own `pins` (bound at whatever render created this callback)
   *   would still be missing the new Page even after that refetch resolves,
   *   since refetching the cache doesn't reach back into an already-created
   *   closure. Read the refetched value straight out of the cache instead.
   * - `selectedViewKeys` / `format`: read from storage, not this instance's
   *   state, so a selection changed in a different mounted instance is
   *   honored rather than silently reverted by whichever instance happens to
   *   write next — `writeManifest`'s prune would otherwise delete the very
   *   files the other instance just asked for.
   * - `handle`: resolved by the caller's `ensureReady()` for the same reason.
   */
  const writeAfterPinsSettle = useCallback(async () => {
    const target = handle ?? (await getStoredDirectoryHandle(tenantId));
    if (!target) return;

    await queryClient.refetchQueries({queryKey: relayKeys.pins.list(tenantId)});
    const freshPins = queryClient.getQueryData<RelayPin[]>(
      relayKeys.pins.list(tenantId)
    );

    await runWrite(target, {
      pins: freshPins ?? pins ?? [],
      selectedViewKeys: new Set(readSelectedViewKeys() ?? []),
      format: readFormat(),
    });
  }, [handle, tenantId, queryClient, runWrite, pins]);

  return {
    isSupported,
    hasFolder: Boolean(handle),
    folderName: handle?.name,
    pages,
    availableViews,
    selectedViewKeys,
    selectedViewCount,
    setViewSelected,
    ensureReady,
    writeAfterPinsSettle,
    format,
    setFormat: setFormatPersisted,
    isWorking,
    progress,
    chooseFolderAndWrite,
    updateInPlace,
    downloadZip,
  };
}
